import { Action, createReducer, on } from '@ngrx/store';
import { Photo } from '@classifieds-sell/trademe-core-ui';
import {
    MotorsMetadata,
    ValidateListingResponse,
    ValidationResult
} from 'libs/datalayer/src';
import { ErrorModel } from '../../common.models';
import { ListingActionResponseModel } from '../models/listing-action-response.model';
import * as DraftActions from './motors.draft.actions';

export const DRAFT_MOTORS_FEATURE_KEY = 'draft-motors';

export interface MotorsDraftState {
    id: number;
    categoryId: number;
    status: number;

    originalDraftData: any;
    draftData: any;
    validationSchema: any;
    metadata: MotorsMetadata;
    validationResults: ValidationResult[];
    validateListingResponse: ValidateListingResponse;

    makeListingActionLoading?: boolean;
    makeListingActionResponse: ListingActionResponseModel;

    draftLoaded: boolean;
    draftLoadedFromLocalStorage: boolean;
    isLoadingDraftSuccess?: boolean;

    // temporary solution for loading status (for saving draft)
    // these will eventually be moved to `Loading State` to be able to keep track of each action?
    // and for GetValidation and GetDraftData as well
    isLoading?: boolean;
    loadingSuccess?: boolean;
    loadingFailure?: boolean;
    error?: ErrorModel | null;
}

export interface MotorsDraftPartialState {
    readonly [DRAFT_MOTORS_FEATURE_KEY]: MotorsDraftState;
}

export const draftInitialStateMotors: MotorsDraftState = {
    id: 0,
    categoryId: 0,
    status: 0,
    originalDraftData: null,
    draftData: null,
    validationSchema: null,
    metadata: {} as MotorsMetadata,
    validationResults: [],
    validateListingResponse: null,

    makeListingActionLoading: false,
    makeListingActionResponse: null,

    draftLoaded: false,

    isLoading: false,
    loadingSuccess: false,
    loadingFailure: false,
    draftLoadedFromLocalStorage: false
};

const draftReducerMotors = createReducer(
    draftInitialStateMotors,
    on(DraftActions.getDraftSuccess, (state, { draft, isFromLocalStorage }) => ({
        ...state,
        ...draft,
        originalDraftData: { ...draft.draftData },
        draftLoaded: true,
        isLoadingDraftSuccess: true,
        draftLoadedFromLocalStorage: isFromLocalStorage
    })),
    on(DraftActions.getDraftFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        isLoadingDraftSuccess: false,
        error
    })),
    on(DraftActions.updateDraftData, (state, { draftData }) => ({
        ...state,
        draftData: draftData
    })),
    on(DraftActions.updateValidationResult, (state, { validationResult }) => ({
        ...state,
        validationResults:
            state.validationResults === null
                ? [validationResult]
                : state.validationResults.some(
                      (x) => x.stepId === validationResult.stepId
                  )
                ? state.validationResults.map((val) => {
                      // update the existing validation step
                      if (val.stepId === validationResult.stepId) {
                          return validationResult;
                      }

                      return val;
                  })
                : [...state.validationResults, validationResult]
    })),
    on(DraftActions.saveDraft, (state) => ({
        ...state,
        isLoading: true,
        loadingSuccess: false,
        loadingFailure: false
    })),
    on(DraftActions.saveDraftSuccess, (state, { result, lastSavedStepId }) => ({
        ...state,
        isLoading: false,
        loadingSuccess: true,
        draftData: {
            ...state.draftData,
            lastSavedStepId
        },
        id: result.draftId,
        status: result.draftStatus
    })),
    on(DraftActions.saveDraftFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        loadingFailure: true,
        error
    })),
    on(DraftActions.resetSaveDraftLoadingStatus, (state) => ({
        ...state,
        loadingSuccess: false,
        loadingFailure: false
    })),
    on(DraftActions.makeListingAction, (state) => ({
        ...state,
        makeListingActionLoading: true
    })),
    on(
        DraftActions.makeListingActionSuccess,
        (state, { listingRequest, listingRequestType }) => ({
            ...state,
            makeListingActionResponse: {
                response: listingRequest,
                type: listingRequestType
            },
            makeListingActionLoading: false
        })
    ),
    on(
        DraftActions.makeListingActionFailure,
        (state, { error, listingRequestType }) => ({
            ...state,
            makeListingActionLoading: false,
            makeListingActionResponse: {
                response: {
                    success: false,
                    description: error?.error?.ErrorDescription ?? error?.message
                },
                type: listingRequestType
            },
            error
        })
    ),
    on(DraftActions.validateListingSuccess, (state, { validListing }) => ({
        ...state,
        validateListingResponse: validListing
    })),
    on(DraftActions.validateListingFailure, (state, { error }) => ({
        ...state,
        validateListingResponse: {
            success: false,
            description: error?.message
        },
        error
    })),
    on(
        DraftActions.addPhotoToDraftData,
        (state, { photo, parentProperty }) => ({
            ...state,
            draftData: {
                ...state.draftData,
                [parentProperty]: {
                    ...state.draftData[parentProperty],
                    photos: [
                        ...(state.draftData[parentProperty].photos || []),
                        photo
                    ]
                }
            }
        })
    ),
    on(
        DraftActions.uploadPhotoSuccess,
        (state, { photoId, photoUrls, parentProperty }) => {
            const photos = [...(state.draftData[parentProperty].photos || [])];

            const index = photos.findIndex((o: Photo) => o.photoId === photoId);
            if (photos[index]) {
                photos[index] = {
                    ...photos[index],
                    ...photoUrls,
                    loading: false
                };
            }
            return {
                ...state,
                draftData: {
                    ...state.draftData,
                    [parentProperty]: {
                        ...state.draftData[parentProperty],
                        photos: [...photos]
                    }
                }
            };
        }
    ),
    on(
        DraftActions.uploadPhotoFailure,
        (state, { photoId, error, parentProperty }) => {
            const photos = [...(state.draftData[parentProperty].photos || [])];

            const index = photos.findIndex((o: Photo) => o.photoId === photoId);
            if (photos[index]) {
                photos[index] = {
                    ...photos[index],
                    loading: false,
                    hasError: true,
                    errorMessage: error.error?.ErrorDescription
                };
            }
            return {
                ...state,
                draftData: {
                    ...state.draftData,
                    [parentProperty]: {
                        ...state.draftData[parentProperty],
                        photos: [...photos]
                    }
                }
            };
        }
    ),
    on(
        DraftActions.updatePhotosInDraftData,
        (state, { photos, parentProperty }) => ({
            ...state,
            draftData: {
                ...state.draftData,
                [parentProperty]: {
                    ...state.draftData[parentProperty],
                    photos: [...photos]
                }
            }
        })
    ),
    on(
        DraftActions.updateTransformPhotoDataToDraftData,
        (state, { editedPhotoData, parentProperty }) => {
            const photos = [...(state.draftData[parentProperty].photos || [])];

            const index = photos.findIndex(
                (o: Photo) => o.photoId === editedPhotoData.photoId
            );
            if (photos[index]) {
                photos[index] = { ...photos[index], loading: true };
            }
            return {
                ...state,
                draftData: {
                    ...state.draftData,
                    [parentProperty]: {
                        ...state.draftData[parentProperty],
                        photos: [...photos]
                    }
                }
            };
        }
    ),
    on(
        DraftActions.transformPhotoSuccess,
        (state, { photoId, transformedPhoto, crop, parentProperty }) => {
            const photos = [...(state.draftData[parentProperty].photos || [])];

            const index = photos.findIndex((o: Photo) => o.photoId === photoId);
            if (photos[index]) {
                photos[index] = {
                    ...photos[index],
                    transformedPhoto: { ...transformedPhoto, crop: crop },
                    loading: false
                };
            }
            return {
                ...state,
                draftData: {
                    ...state.draftData,
                    [parentProperty]: {
                        ...state.draftData[parentProperty],
                        photos: [...photos]
                    }
                }
            };
        }
    ),
    on(
        DraftActions.transformPhotoFailure,
        (state, { photoId, error, parentProperty }) => {
            const photos = [...(state.draftData[parentProperty].photos || [])];

            const index = photos.findIndex((o: Photo) => o.photoId === photoId);
            if (photos[index]) {
                photos[index] = {
                    ...photos[index],
                    loading: false,
                    hasError: true,
                    errorMessage: error.error?.ErrorDescription
                };
            }
            return {
                ...state,
                draftData: {
                    ...state.draftData,
                    [parentProperty]: {
                        ...state.draftData[parentProperty],
                        photos: [...photos]
                    }
                }
            };
        }
    ),
    on(
        DraftActions.updateInvalidPhoto,
        (state, { photoId, errorMessage, parentProperty }) => {
            const photos = [...(state.draftData[parentProperty].photos || [])];

            const index = photos.findIndex((o: Photo) => o.photoId === photoId);
            if (photos[index]) {
                photos[index] = {
                    ...photos[index],
                    loading: false,
                    hasError: true,
                    errorMessage: errorMessage
                };
            }
            return {
                ...state,
                draftData: {
                    ...state.draftData,
                    [parentProperty]: {
                        ...state.draftData[parentProperty],
                        photos: [...photos]
                    }
                }
            };
        }
    ),
    on(DraftActions.updateCurrentStepSuccess, (state, { currentStepId }) => ({
        ...state,
        draftData: {
            ...state.draftData,
            currentStepId
        }
    })),
    on(
        DraftActions.resetPackageCodeIfNeeded,
        (state, { isRelist }) => {
            if (state.draftLoaded) {
                let ret = { ...state };
                if (isRelist) {
                    let promote = {
                        ...ret.draftData.listingDetails,
                        categoryPackageId: null
                    };
                    return {
                        ...ret,
                        // spread computed promote object
                        draftData: {
                            ...ret.draftData,
                            listingDetails: {
                                ...promote
                            }
                        }
                    };
                }
            }
            return state;
        }
    )
);

export function draftReducerMotorsFunc(
    state: MotorsDraftState | undefined,
    action: Action
) {
    return draftReducerMotors(state, action);
}
